import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const BestWebsiteBuilderLandscapers = () => {
  const comparisonData = [
    { feature: "Portfolio Display", optionA: "Squarespace", optionB: "Beautiful galleries" },
    { feature: "SEO Power", optionA: "WordPress", optionB: "Unlimited optimization" },
    { feature: "Ease of Use", optionA: "Wix", optionB: "Simplest interface" },
    { feature: "Cost-Effectiveness", optionA: "WordPress", optionB: "Best long-term value" },
    { feature: "Lead Forms", optionA: "WordPress", optionB: "Advanced builders" },
    { feature: "Local SEO", optionA: "WordPress", optionB: "Schema + citations" },
    { feature: "Mobile Experience", optionA: "All comparable", optionB: "Depends on execution" },
    { feature: "Project Galleries", optionA: "Squarespace", optionB: "Built-in excellence" },
  ];

  const verdicts = [
    { label: "Best for Portfolios", recommendation: "Squarespace" },
    { label: "Best for Leads", recommendation: "WordPress" },
    { label: "Easiest Setup", recommendation: "Wix" },
    { label: "Best Overall", recommendation: "WordPress or Done-for-you" },
    { label: "Best Value", recommendation: "Done-for-you subscription" },
  ];

  const faqs = [
    {
      question: "What website builder do successful landscapers use?",
      answer: "Most successful landscaping companies use WordPress or professional custom sites. The control and SEO capabilities help them dominate local search results and generate consistent leads."
    },
    {
      question: "Is Squarespace good for landscaping websites?",
      answer: "Squarespace excels at showcasing landscaping portfolios with beautiful galleries. However, it lacks advanced SEO features that help generate organic leads from Google searches."
    },
    {
      question: "How important is SEO for a landscaping website?",
      answer: "Critical. 97% of consumers search online for local services. A website that ranks well for 'landscaping near me' and related terms generates free, ongoing leads without advertising costs."
    },
    {
      question: "Can I use Wix for my landscaping business?",
      answer: "Wix works for basic online presence but limits your growth potential. Landscaping companies that want to compete seriously for local keywords typically need WordPress or professional solutions."
    },
    {
      question: "How much should I spend on a landscaping website?",
      answer: "DIY costs $0-$500/year but often underperforms. Professional builds range $3,000-$15,000 one-time. Website subscriptions offer professional quality for $150-$500/month with ongoing support."
    },
    {
      question: "What features do landscaping websites need?",
      answer: "Essential: project galleries, service pages, service area info, quote forms, mobile responsiveness, fast loading, Google reviews integration, and proper local SEO structure."
    },
    {
      question: "Should I show my work on my website?",
      answer: "Absolutely. Before/after photos and project galleries are the #1 conversion factor for landscaping websites. Potential clients want to see your quality before calling."
    },
    {
      question: "How do I get my landscaping website to rank locally?",
      answer: "Optimize for local keywords, create service area pages, claim your Google Business Profile, get customer reviews, and ensure your website has proper schema markup and NAP consistency."
    },
    {
      question: "Is WordPress too complicated for landscaping businesses?",
      answer: "Modern WordPress with page builders is user-friendly. However, for best results without technical hassle, a done-for-you website subscription handles everything professionally."
    },
    {
      question: "What's the ROI of a professional landscaping website?",
      answer: "A well-optimized landscaping website can generate 5-20+ leads per month worth $5,000-$50,000+ in project value. The ROI often exceeds 500% annually for growing companies."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/best-website-builder-landscapers/";

  return (
    <>
      <SEO 
        title="Best Website Builder for Landscapers 2024"
        description="Find the best website builder for landscaping businesses. Compare WordPress, Squarespace, Wix for showcasing projects and generating leads."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="Best Website Builder for Landscapers"
        description="Find the best website builder for landscaping businesses. Compare platforms for showcasing projects and generating leads."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Best Website Builder for Landscapers in 2024
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Landscaping businesses need websites that showcase stunning work AND generate leads. Here's which builders deliver both.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Platform Comparison for Landscapers</h2>
              <p className="text-muted-foreground mb-6">
                Your <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> needs 
                to balance visual impact with lead generation. Here's how top platforms compare:
              </p>
              <ComparisonTable 
                optionAName="Best For"
                optionBName="Why"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Squarespace: Best for Visual Portfolios</h2>
              <p className="text-muted-foreground mb-4">
                Squarespace shines when showcasing beautiful landscaping projects. Its gallery templates are industry-leading 
                for visual presentation.
              </p>
              <div className="bg-primary/10 p-6 rounded-lg mb-4">
                <h3 className="font-bold mb-3">Squarespace Strengths:</h3>
                <ul className="space-y-2">
                  <li>• Stunning project gallery layouts</li>
                  <li>• Consistent, professional design</li>
                  <li>• Easy image management</li>
                  <li>• Built-in SEO basics</li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                However, for serious <NavLink to="/landscaping-website-seo/" className="text-primary hover:underline">SEO optimization</NavLink>, 
                Squarespace hits a ceiling that limits organic lead generation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">WordPress: Best for Lead Generation</h2>
              <p className="text-muted-foreground mb-4">
                WordPress dominates for landscapers serious about <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">ranking on Google</NavLink> and 
                generating consistent leads.
              </p>
              <div className="bg-primary/10 p-6 rounded-lg">
                <h3 className="font-bold mb-3">WordPress Advantages:</h3>
                <ul className="space-y-2">
                  <li>• Full SEO control for local keywords</li>
                  <li>• Advanced <NavLink to="/landscaping-quote-form/" className="text-primary hover:underline">quote form builders</NavLink></li>
                  <li>• Unlimited <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area pages</NavLink></li>
                  <li>• Speed optimization potential</li>
                  <li>• Schema markup for rich snippets</li>
                </ul>
              </div>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Winning Choice: Done-For-You Websites</h2>
              <p className="text-muted-foreground mb-4">
                Why compromise when you can have the best of everything? Our 
                <NavLink to="/websites/" className="text-primary hover:underline"> landscaping website subscription</NavLink> combines 
                stunning portfolios with SEO power.
              </p>
              <p className="text-muted-foreground mb-4">
                We build sites that showcase your <NavLink to="/landscaping-website-examples/" className="text-primary hover:underline">best work</NavLink> while 
                ranking for profitable keywords in your service areas.
              </p>
              <p className="text-muted-foreground">
                Add <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile optimization</NavLink> for 
                map pack dominance, or <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads</NavLink> for 
                immediate leads.
              </p>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/squarespace-vs-wordpress-landscaping/", text: "Squarespace vs WordPress Comparison" },
                { href: "/comparisons/best-website-builder-for-lawn-care/", text: "Best Builders for Lawn Care" },
                { href: "/landscaping-website-cost/", text: "Landscaping Website Cost Guide" },
                { href: "/landscaping-website-content/", text: "Website Content Guide" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default BestWebsiteBuilderLandscapers;
